import { useState, useEffect, useCallback } from 'react'
import { Popover } from '@headlessui/react'
import { usePopper } from 'react-popper'
import { useDatePicker } from '@rehookify/datepicker'
import { format, setHours, setMinutes } from 'date-fns'
import clsx from 'clsx'
import Button from './Button'
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from './icons'
import './DateRangePicker.css'

export interface DateRange {
    start: Date
    end: Date
}

export interface DateRangePickerProps {
    value: DateRange
    onChange: (range: DateRange) => void
    maxDate?: Date
    minDate?: Date
    showTime?: boolean
}

function formatTriggerLabel(range: DateRange, showTime: boolean): string {
    const { start, end } = range
    const sameYear = start.getFullYear() === end.getFullYear()
    if (showTime) {
        const s = format(start, 'MMM d, h:mm a')
        const e = format(end, sameYear ? 'MMM d, h:mm a' : 'MMM d yyyy, h:mm a')
        return sameYear ? `${s} – ${e}, ${start.getFullYear()}` : `${s} – ${e}`
    }
    const s = format(start, 'MMM d')
    const e = format(end, sameYear ? 'MMM d' : 'MMM d, yyyy')
    return sameYear ? `${s} – ${e}, ${start.getFullYear()}` : `${s} – ${e}`
}

export default function DateRangePicker({ value, onChange, maxDate, minDate, showTime = true }: DateRangePickerProps) {
    const [referenceElement, setReferenceElement] = useState<Element | null>(null)
    const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [{ name: 'offset', options: { offset: [0, 4] } }],
    })

    // Track the raw selectedDates array from the library so range mode works properly.
    // When length is 1, user has picked start and is picking end.
    // When length is 2, a complete range is selected.
    const [selectedDates, setSelectedDates] = useState<Date[]>([value.start, value.end])
    const [startTime, setStartTime] = useState(format(value.start, 'HH:mm'))
    const [endTime, setEndTime] = useState(format(value.end, 'HH:mm'))
    const [isOpen, setIsOpen] = useState(false)

    const syncDraft = useCallback(() => {
        setSelectedDates([value.start, value.end])
        setStartTime(format(value.start, 'HH:mm'))
        setEndTime(format(value.end, 'HH:mm'))
    }, [value])

    // Sync when value changes externally while closed
    useEffect(() => {
        if (!isOpen) syncDraft()
    }, [value, isOpen, syncDraft])

    // Calendar hook - pass raw array so library tracks range state correctly
    const { data, propGetters } = useDatePicker({
        selectedDates,
        onDatesChange: setSelectedDates,
        dates: {
            mode: 'range',
            ...(maxDate ? { maxDate } : {}),
            ...(minDate ? { minDate } : {}),
        },
        calendar: {
            startDay: 1,
        },
    })

    const calendar = data.calendars[0]
    const weekDays = data.weekDays

    // Derive display dates from selectedDates
    const displayStart = selectedDates[0] ?? value.start
    const displayEnd = selectedDates[1] ?? selectedDates[0] ?? value.end

    const handleApply = (close: () => void) => {
        const start = selectedDates[0] ?? value.start
        const end = selectedDates[1] ?? selectedDates[0] ?? value.end
        const [sh, sm] = startTime.split(':').map(Number)
        const [eh, em] = endTime.split(':').map(Number)
        const finalStart = setMinutes(setHours(start, isNaN(sh) ? 0 : sh), isNaN(sm) ? 0 : sm)
        const finalEnd = setMinutes(setHours(end, isNaN(eh) ? 23 : eh), isNaN(em) ? 59 : em)
        onChange({ start: finalStart, end: finalEnd })
        close()
    }

    const handleCancel = (close: () => void) => {
        syncDraft()
        close()
    }

    return (
        <Popover>
            {({ close, open }) => {
                if (open !== isOpen) {
                    setTimeout(() => setIsOpen(open), 0)
                    if (open) setTimeout(() => syncDraft(), 0)
                }

                return (
                    <>
                        <Popover.Button as="div" ref={setReferenceElement}>
                            <Button size="small" variant="secondary" icon={<CalendarIcon />}>
                                {formatTriggerLabel(value, showTime)}
                            </Button>
                        </Popover.Button>
                        <Popover.Panel
                            ref={setPopperElement}
                            style={styles.popper as any}
                            {...attributes.popper}
                            className="ui-drp-panel"
                        >
                            <form onSubmit={e => { e.preventDefault(); handleApply(close) }}>
                                {/* Date/Time Inputs */}
                                <div className="ui-drp-inputs">
                                    <div className="ui-drp-input-row">
                                        <span className="ui-drp-label">Start</span>
                                        <input
                                            type="text"
                                            readOnly
                                            value={format(displayStart, 'MMM d, yyyy')}
                                        />
                                        {showTime && (
                                            <input
                                                type="time"
                                                value={startTime}
                                                onChange={e => setStartTime(e.target.value)}
                                            />
                                        )}
                                    </div>
                                    <div className="ui-drp-input-row">
                                        <span className="ui-drp-label">End</span>
                                        <input
                                            type="text"
                                            readOnly
                                            value={format(displayEnd, 'MMM d, yyyy')}
                                        />
                                        {showTime && (
                                            <input
                                                type="time"
                                                value={endTime}
                                                onChange={e => setEndTime(e.target.value)}
                                            />
                                        )}
                                    </div>
                                </div>

                                {/* Calendar */}
                                <div className="ui-drp-calendar-header">
                                    <button type="button" {...propGetters.previousMonthButton()}>
                                        <ChevronLeftIcon />
                                    </button>
                                    <span className="ui-drp-calendar-title">
                                        {calendar.month} {calendar.year}
                                    </span>
                                    <button type="button" {...propGetters.nextMonthButton()}>
                                        <ChevronRightIcon />
                                    </button>
                                </div>

                                <div className="ui-drp-weekdays">
                                    {weekDays.map(day => (
                                        <span key={day}>{day}</span>
                                    ))}
                                </div>

                                <div className="ui-drp-days">
                                    {calendar.days.map((day) => (
                                        <button
                                            key={day.date}
                                            type="button"
                                            className={clsx('ui-drp-day', {
                                                outside: !day.inCurrentMonth,
                                                disabled: day.disabled,
                                                today: day.isToday,
                                                'in-range': day.range === 'in-range',
                                                'range-start': day.range.includes('range-start'),
                                                'range-end': day.range.includes('range-end'),
                                                'will-be-in-range': day.range === 'will-be-in-range',
                                                'will-be-range-start': day.range === 'will-be-range-start',
                                                'will-be-range-end': day.range === 'will-be-range-end',
                                            })}
                                            {...propGetters.dayButton(day)}
                                        >
                                            {day.day}
                                        </button>
                                    ))}
                                </div>

                                {/* Footer */}
                                <div className="ui-drp-footer">
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        type="button"
                                        onClick={() => handleCancel(close)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        size="small"
                                        type="submit"
                                    >
                                        Apply
                                    </Button>
                                </div>
                            </form>
                        </Popover.Panel>
                    </>
                )
            }}
        </Popover>
    )
}
